/**
 * Mock data for study feature (科目一覧).
 * Language-aware: reads i18n.language to return localized domain data.
 * Subjects from the construction industry field.
 */

import i18n from "@/i18n/config";
import {
  TStudySubject,
  TStudyPageData,
  TDiagnosisQuizSession,
  TWordPracticeSession,
  TWordQuizQuestion,
  TWordQuizSession,
  TLevelPracticeSession,
  TDiagnosisQuestion,
  TTextExplanationSession,
  TRecommendation,
  RECOMMENDATION_TYPE,
} from "@/infrastructure/adapters/study/study.types";

/** Simulate network delay */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Helper: pick localized string by current language, fallback to ja */
const t = (map: Record<string, string>): string => map[i18n.language] ?? map.ja;

// ─── Localized domain label maps ───────────────────────────────

/** Subject names — always Japanese because learners study Japanese content */
const SUBJECT_NAMES = ["建設基礎知識", "安全管理", "労働法規", "建設機械", "施工管理", "品質管理"];

/** Practice level names — domain labels, should be translated */
const LEVEL_NAMES: Record<string, string>[] = [
  { ja: "言葉の意味", en: "Word Meaning", vi: "Nghĩa của từ", my: "စကားလုံးအဓိပ္ပာယ်", id: "Arti Kata" },
  { ja: "穴埋め問題", en: "Fill in the Blank", vi: "Điền vào chỗ trống", my: "ကွက်လပ်ဖြည့်ပါ", id: "Isi Bagian Kosong" },
  { ja: "一問一答", en: "Q&A", vi: "Hỏi đáp", my: "မေးခွန်းနှင့်အဖြေ", id: "Tanya Jawab" },
  { ja: "2文整合", en: "Sentence Matching", vi: "Ghép câu", my: "ဝါကျတိုက်ဆိုင်ခြင်း", id: "Pencocokan Kalimat" },
  { ja: "総合問題", en: "Comprehensive", vi: "Tổng hợp", my: "ဘက်စုံ", id: "Komprehensif" },
];

/** Word translations per language (learner's native language) */
const WORD_TRANSLATIONS: Record<string, string>[] = [
  { ja: "基礎", en: "Foundation / Base", vi: "Nền móng", my: "အခြေခံ", id: "Fondasi" },
  { ja: "足場", en: "Scaffolding", vi: "Giàn giáo", my: "ဆောက်လုပ်ရေးလင်းကန့်", id: "Perancah" },
  { ja: "安全帯", en: "Safety belt", vi: "Dây an toàn", my: "ဘေးကင်းခါးပတ်", id: "Sabuk Pengaman" },
  { ja: "型枠", en: "Formwork", vi: "Khuôn đúc", my: "ပုံစံခွက်", id: "Bekisting" },
  { ja: "鉄筋", en: "Rebar", vi: "Cốt thép", my: "သံချောင်း", id: "Besi Tulangan" },
  { ja: "養生", en: "Curing / Protection", vi: "Bảo dưỡng", my: "ပြုစုခြင်း", id: "Perawatan" },
  { ja: "墨出し", en: "Marking lines", vi: "Kẻ vạch", my: "မျဉ်းသားခြင်း", id: "Penandaan Garis" },
  { ja: "建材", en: "Building materials", vi: "Vật liệu xây dựng", my: "ဆောက်လုပ်ရေးပစ္စည်း", id: "Bahan Bangunan" },
  { ja: "施工", en: "Construction", vi: "Thi công", my: "ဆောက်လုပ်ခြင်း", id: "Konstruksi" },
  { ja: "現場", en: "Work site", vi: "Công trường", my: "လုပ်ငန်းခွင်", id: "Lokasi Kerja" },
  { ja: "配管", en: "Piping", vi: "Đường ống", my: "ပိုက်လိုင်း", id: "Perpipaan" },
  { ja: "電気工事", en: "Electrical work", vi: "Công trình điện", my: "လျှပ်စစ်အလုပ်", id: "Pekerjaan Listrik" },
  { ja: "塗装", en: "Painting", vi: "Sơn", my: "ဆေးသုတ်ခြင်း", id: "Pengecatan" },
  { ja: "防水", en: "Waterproofing", vi: "Chống thấm", my: "ရေစိမ့်ကာကွယ်ခြင်း", id: "Waterproofing" },
  { ja: "断熱", en: "Insulation", vi: "Cách nhiệt", my: "အပူကာကွယ်ခြင်း", id: "Insulasi" },
  { ja: "解体", en: "Demolition", vi: "Phá dỡ", my: "ဖြိုဖျက်ခြင်း", id: "Pembongkaran" },
  { ja: "測量", en: "Surveying", vi: "Đo đạc", my: "တိုင်းတာခြင်း", id: "Survei" },
  { ja: "図面", en: "Blueprint / Drawing", vi: "Bản vẽ", my: "ပုံကြမ်း", id: "Gambar Kerja" },
  { ja: "重機", en: "Heavy machinery", vi: "Máy móc nặng", my: "အကြီးစားစက်ယန္တရား", id: "Alat Berat" },
  { ja: "安全管理", en: "Safety management", vi: "Quản lý an toàn", my: "ဘေးကင်းရေးစီမံခန့်ခွဲမှု", id: "Manajemen Keselamatan" },
];

// ─── Mock data builders (language-aware) ───────────────────────

/** Build subjects list (names always Japanese — it's Japanese study content) */
const getSubjects = (): TStudySubject[] => [
  { id: "1", name: SUBJECT_NAMES[0], isCompleted: true, hasLevelAssessment: true, hasAnswered: true, isLocked: false },
  { id: "2", name: SUBJECT_NAMES[1], isCompleted: true, hasLevelAssessment: true, hasAnswered: true, isLocked: false },
  { id: "3", name: SUBJECT_NAMES[2], isCompleted: false, hasLevelAssessment: true, hasAnswered: false, isLocked: false },
  { id: "4", name: SUBJECT_NAMES[3], isCompleted: false, hasLevelAssessment: false, hasAnswered: false, isLocked: false },
  { id: "5", name: SUBJECT_NAMES[4], isCompleted: false, hasLevelAssessment: false, hasAnswered: false, isLocked: false },
  { id: "6", name: SUBJECT_NAMES[5], isCompleted: false, hasLevelAssessment: false, hasAnswered: false, isLocked: false },
];

/** Build word practice items with localized translations */
const getWordPracticeWords = (): TWordPracticeSession["words"] => {
  const kanjis = [
    "基礎",
    "足場",
    "安全帯",
    "型枠",
    "鉄筋",
    "養生",
    "墨出し",
    "建材",
    "施工",
    "現場",
    "配管",
    "電気工事",
    "塗装",
    "防水",
    "断熱",
    "解体",
    "測量",
    "図面",
    "重機",
    "安全管理",
  ];
  const furiganas = [
    "きそ",
    "あしば",
    "あんぜんたい",
    "かたわく",
    "てっきん",
    "ようじょう",
    "すみだし",
    "けんざい",
    "せこう",
    "げんば",
    "はいかん",
    "でんきこうじ",
    "とそう",
    "ぼうすい",
    "だんねつ",
    "かいたい",
    "そくりょう",
    "ずめん",
    "じゅうき",
    "あんぜんかんり",
  ];
  return kanjis.map((kanji, i) => ({
    id: i + 1,
    kanji,
    furigana: furiganas[i],
    translation: t(WORD_TRANSLATIONS[i]),
  }));
};

// ─── Exported mock API functions ───────────────────────────────

/** Fetch study page data (subjects + recommendation) */
export const getStudyPageDataMock = async (): Promise<TStudyPageData> => {
  await delay(100);
  return {
    recommendation: null,
    subjects: getSubjects(),
    isTrial: false,
  };
};

/** Fetch diagnosis quiz session for a subject (always Japanese — testing JP proficiency) */
export const getDiagnosisQuizSessionMock = async (): Promise<TDiagnosisQuizSession> => {
  await delay(100);
  return {
    questions: [
      {
        questionText: "「基礎」の意味は次のうちどれか？",
        options: [{ text: "建物を支える土台部分" }, { text: "建物の屋根" }, { text: "建物の外壁" }, { text: "建物の窓" }],
        correctIndex: 0,
        type: "vocabulary",
        level: 1,
        explanation: "基礎は建物の重量を地面に伝え、建物を安定させる非常に重要な部分である。",
      },
      {
        questionText: "「足場」の意味は次のうちどれか？",
        options: [{ text: "建物の基礎" }, { text: "高所作業用の仮設構造物" }, { text: "建設用の重機" }, { text: "建築材料の一種" }],
        correctIndex: 1,
        type: "vocabulary",
        level: 1,
        explanation: "足場は高所での建設作業を安全に行うために設置される仮設の構造物である。",
      },
      {
        questionText: "「安全帯」の意味は次のうちどれか？",
        options: [{ text: "安全な場所" }, { text: "高所作業時に使用する落下防止具" }, { text: "安全を確認する装置" }, { text: "安全に関する法律" }],
        correctIndex: 1,
        type: "vocabulary",
        level: 2,
        explanation: "安全帯は高所作業時に作業者の落下を防止するために使用する保護具である。",
      },
      {
        questionText: "「型枠」の意味は次のうちどれか？",
        options: [{ text: "鉄筋の型" }, { text: "コンクリートを流し込む枠組み" }, { text: "建物の設計図" }, { text: "工事用の道具箱" }],
        correctIndex: 1,
        type: "vocabulary",
        level: 2,
        explanation: "型枠はコンクリートを所定の形に成形するために使用する枠組みである。",
      },
      {
        questionText: "「鉄筋」の意味は次のうちどれか？",
        options: [{ text: "鉄で作った柱" }, { text: "コンクリート内の補強用の鋼材" }, { text: "鉄製の工具" }, { text: "鉄骨構造の建物" }],
        correctIndex: 1,
        type: "vocabulary",
        level: 3,
        explanation: "鉄筋はコンクリートの引張強度を補うために使用される鋼材である。",
      },
      {
        questionText: "「養生」の意味は次のうちどれか？",
        options: [
          { text: "休養すること" },
          { text: "コンクリートの品質を保つための保護作業" },
          { text: "建材を運搬すること" },
          { text: "現場を清掃すること" },
        ],
        correctIndex: 1,
        type: "vocabulary",
        level: 3,
        explanation: "養生はコンクリートが適切に硬化するよう、温度や湿度を管理する保護作業である。",
      },
      {
        questionText: "「墨出し」の意味は次のうちどれか？",
        options: [
          { text: "インクを使う作業" },
          { text: "建物の位置や高さを示す基準線を引く作業" },
          { text: "壁を塗装する作業" },
          { text: "図面を描く作業" },
        ],
        correctIndex: 1,
        type: "vocabulary",
        level: 4,
        explanation: "墨出しは建築工事において、建物の位置や壁・柱の中心線を示す基準線を引く作業である。",
      },
      {
        questionText: "建物の基礎工事について、正しい記述はどれか。",
        options: [
          { text: "基礎工事は天候に関係なく行える" },
          { text: "基礎は建物の重量を地面に伝え安定させる重要な部分" },
          { text: "基礎工事は最後に行う工程である" },
          { text: "基礎の深さは建物の高さに関係ない" },
        ],
        correctIndex: 1,
        type: "practice",
        level: 4,
        explanation: "基礎は建物の重量を地面に伝え、建物を安定させる非常に重要な部分である。",
      },
      {
        questionText: "労働安全衛生法について、正しい記述はどれか。",
        options: [
          { text: "外国人労働者には適用されない" },
          { text: "事業者は労働者の安全と健康を確保する義務がある" },
          { text: "小規模現場では適用が免除される" },
          { text: "安全教育は任意である" },
        ],
        correctIndex: 1,
        type: "practice",
        level: 5,
        explanation: "労働安全衛生法は、事業者に対し労働者の安全と健康を確保するための措置を義務付けている。",
      },
      {
        questionText: "建設現場での高所作業について、正しい記述はどれか。",
        options: [
          { text: "2m以上の高所では墜落防止措置が必要" },
          { text: "安全帯は3m以上から使用する" },
          { text: "足場の点検は月1回で十分である" },
          { text: "高所作業に特別な資格は不要である" },
        ],
        correctIndex: 0,
        type: "practice",
        level: 5,
        explanation: "労働安全衛生規則により、2m以上の高所作業では墜落防止のための措置が必要とされている。",
      },
    ],
    totalCount: 10,
  };
};

/** Generate word quiz session from practice words (pick 5, create 4-choice questions) */
export const getWordQuizSessionMock = async (): Promise<TWordQuizSession> => {
  await delay(100);
  const allWords = getWordPracticeWords();
  const quizWords = allWords.slice(0, 5);
  const allTranslations = allWords.map((w) => w.translation);
  const questions: TWordQuizQuestion[] = quizWords.map((word) => {
    const distractors = allTranslations.filter((t) => t !== word.translation).slice(0, 3);
    const options = [word.translation, ...distractors];
    // Shuffle options
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    return { word, options, correctIndex: options.indexOf(word.translation) };
  });
  return { questions, totalCount: questions.length };
};

/** Level practice questions grouped by level (always Japanese — testing JP proficiency) */
const LEVEL_PRACTICE_QUESTIONS: Record<number, TDiagnosisQuestion[]> = {
  1: [
    {
      questionText: "「基礎」の意味は次のうちどれか？",
      options: [{ text: "建物を支える土台部分" }, { text: "建物の屋根" }, { text: "建物の外壁" }, { text: "建物の窓" }],
      correctIndex: 0,
      type: "vocabulary",
      level: 1,
      explanation: "基礎は建物の重量を地面に伝え、建物を安定させる非常に重要な部分である。",
    },
    {
      questionText: "次の画像のうち建材はどれか？",
      imageFile: "",
      options: [{ text: "木材" }, { text: "ヘルメット" }, { text: "安全帯" }, { text: "作業服" }],
      correctIndex: 0,
      type: "practice",
      level: 1,
      explanation: "木材は建築工事で使用される代表的な建材の一つである。",
    },
    {
      questionText: "「建材」の意味は次のうちどれか？",
      options: [{ text: "建物の設計図" }, { text: "建築に使用する材料" }, { text: "建設現場" }, { text: "建物の構造" }],
      correctIndex: 1,
      type: "vocabulary",
      level: 1,
      explanation: "建材は建築工事で使用される木材、鉄骨、コンクリートなどの材料の総称である。",
    },
  ],
  2: [
    {
      questionText: "「安全帯」の意味は次のうちどれか？",
      options: [{ text: "安全な場所" }, { text: "高所作業時に使用する落下防止具" }, { text: "安全を確認する装置" }, { text: "安全に関する法律" }],
      correctIndex: 1,
      type: "vocabulary",
      level: 2,
      explanation: "安全帯は高所作業時に作業者の落下を防止するために使用する保護具である。",
    },
    {
      questionText: "「型枠」の意味は次のうちどれか？",
      options: [{ text: "鉄筋の型" }, { text: "コンクリートを流し込む枠組み" }, { text: "建物の設計図" }, { text: "工事用の道具箱" }],
      correctIndex: 1,
      type: "vocabulary",
      level: 2,
      explanation: "型枠はコンクリートを所定の形に成形するために使用する枠組みである。",
    },
    {
      questionText: "「施工」の意味として正しいものはどれか？",
      options: [{ text: "設計すること" }, { text: "実際に工事を行うこと" }, { text: "材料を運搬すること" }, { text: "図面を確認すること" }],
      correctIndex: 1,
      type: "vocabulary",
      level: 2,
      explanation: "施工は設計図に基づいて実際に建設工事を行うことを意味する。",
    },
  ],
  3: [
    {
      questionText: "「鉄筋」の意味は次のうちどれか？",
      options: [{ text: "鉄で作った柱" }, { text: "コンクリート内の補強用の鋼材" }, { text: "鉄製の工具" }, { text: "鉄骨構造の建物" }],
      correctIndex: 1,
      type: "vocabulary",
      level: 3,
      explanation: "鉄筋はコンクリートの引張強度を補うために使用される鋼材である。",
    },
    {
      questionText: "次の画像のうち工具はどれか？",
      imageFile: "",
      options: [{ text: "スパナ" }, { text: "ヘルメット" }, { text: "安全帯" }, { text: "足場板" }],
      correctIndex: 0,
      type: "practice",
      level: 3,
      explanation: "スパナはボルトやナットを締めたり緩めたりするための工具である。",
    },
    {
      questionText: "「配管」の意味として正しいものはどれか？",
      options: [
        { text: "電線を配置する作業" },
        { text: "水やガスの管を設置する作業" },
        { text: "建材を配置する作業" },
        { text: "図面を配布する作業" },
      ],
      correctIndex: 1,
      type: "vocabulary",
      level: 3,
      explanation: "配管は建物内に水道管やガス管などを設置する作業のことである。",
    },
  ],
  4: [
    {
      questionText: "「墨出し」の意味は次のうちどれか？",
      options: [
        { text: "インクを使う作業" },
        { text: "建物の位置や高さを示す基準線を引く作業" },
        { text: "壁を塗装する作業" },
        { text: "図面を描く作業" },
      ],
      correctIndex: 1,
      type: "vocabulary",
      level: 4,
      explanation: "墨出しは建築工事において、建物の位置や壁・柱の中心線を示す基準線を引く作業である。",
    },
    {
      questionText: "建物の基礎工事について、正しい記述はどれか。",
      options: [
        { text: "基礎工事は天候に関係なく行える" },
        { text: "基礎は建物の重量を地面に伝え安定させる重要な部分" },
        { text: "基礎工事は最後に行う工程である" },
        { text: "基礎の深さは建物の高さに関係ない" },
      ],
      correctIndex: 1,
      type: "practice",
      level: 4,
      explanation: "基礎は建物の重量を地面に伝え、建物を安定させる非常に重要な部分である。",
    },
    {
      questionText: "「測量」の意味として正しいものはどれか？",
      options: [
        { text: "建物の高さを推測すること" },
        { text: "土地や建物の位置・形状を測定すること" },
        { text: "材料の品質を検査すること" },
        { text: "工事の進捗を記録すること" },
      ],
      correctIndex: 1,
      type: "vocabulary",
      level: 4,
      explanation: "測量は土地や建物の位置、距離、面積、高さなどを正確に測定する作業である。",
    },
  ],
  5: [
    {
      questionText: "労働安全衛生法について、正しい記述はどれか。",
      options: [
        { text: "外国人労働者には適用されない" },
        { text: "事業者は労働者の安全と健康を確保する義務がある" },
        { text: "小規模現場では適用が免除される" },
        { text: "安全教育は任意である" },
      ],
      correctIndex: 1,
      type: "practice",
      level: 5,
      explanation: "労働安全衛生法は、事業者に対し労働者の安全と健康を確保するための措置を義務付けている。",
    },
    {
      questionText: "次の画像が示す保護具の名称はどれか？",
      imageFile: "",
      options: [{ text: "フルハーネス型安全帯" }, { text: "防塵マスク" }, { text: "保護メガネ" }, { text: "安全靴" }],
      correctIndex: 0,
      type: "practice",
      level: 5,
      explanation: "フルハーネス型安全帯は高所作業時に全身を支持し、墜落を防止するための保護具である。",
    },
    {
      questionText: "建設現場の安全管理について、正しい記述はどれか。",
      options: [
        { text: "作業前のKY活動は省略できる" },
        { text: "安全管理者は毎日現場を巡回する必要がある" },
        { text: "ヘルメットの着用は推奨だが義務ではない" },
        { text: "安全教育は年1回で十分である" },
      ],
      correctIndex: 1,
      type: "practice",
      level: 5,
      explanation: "安全管理者は毎日現場を巡回し、危険箇所の確認や作業員の安全行動を監視する必要がある。",
    },
  ],
};

/** Fetch level practice session for a subject + level */
export const getLevelPracticeSessionMock = async (_subjectId: string, level: number): Promise<TLevelPracticeSession> => {
  await delay(100);
  const questions = LEVEL_PRACTICE_QUESTIONS[level] ?? LEVEL_PRACTICE_QUESTIONS[1];
  const levelNameMap = LEVEL_NAMES[level - 1] ?? LEVEL_NAMES[0];
  return {
    level,
    levelName: t(levelNameMap),
    questions,
    totalCount: questions.length,
  };
};

/** Fetch text explanation session (テキスト解説を閲覧) — 4 pages of construction knowledge */
export const getTextExplanationSessionMock = async (): Promise<TTextExplanationSession> => {
  await delay(100);
  return {
    subjectName: "建設基礎知識",
    pages: [
      "建設基礎知識\n第1章 建設基礎知識の基礎\n\nこの章では、建設基礎知識の基本的な概念と重要性について学びます。\n\n1.1 はじめに\n建設基礎知識は建設現場において非常に重要な分野です。\n\n1.2 基本用語\n以下の用語を覚えましょう：\n・施工 (せこう) - 工事を行うこと\n・安全帯 (あんぜんたい) - 高所作業用の安全装置\n・足場 (あしば) - 作業用の仮設構造物",
      "第2章 基礎工事の基本\n\n基礎工事は建物を建てる上で最も重要な工程の一つです。基礎は建物の重量を地面に伝え、建物を安定させる役割を果たします。\n\n2.1 基礎の種類\n・直接基礎（べた基礎、布基礎）\n  地盤が良好な場合に使用されます。\n・杭基礎\n  地盤が軟弱な場合に使用されます。\n\n2.2 基礎工事の手順\n1. 地盤調査\n2. 根切り（掘削）\n3. 砕石敷き\n4. 防湿シート敷設\n5. 配筋\n6. コンクリート打設\n7. 養生",
      "第3章 安全管理の基本\n\n建設現場での安全管理は、労働者の命と健康を守るために最も重要です。\n\n3.1 安全管理の三原則\n1. 整理・整頓・清掃（3S活動）\n2. 危険予知活動（KY活動）\n3. ヒヤリハット報告\n\n3.2 高所作業の安全対策\n・2m以上の高所作業では墜落防止措置が必要\n・安全帯（フルハーネス型）の着用義務\n・足場の定期点検\n・作業前の安全確認\n\n3.3 保護具の種類\n・ヘルメット（保護帽）\n・安全靴\n・安全帯\n・保護メガネ\n・防塵マスク",
      "第4章 建設用語の基礎\n\n建設現場でよく使われる基本用語を覚えましょう。\n\n【構造関連】\n・躯体（くたい）：建物の骨組み部分\n・鉄筋（てっきん）：コンクリート内の補強鋼材\n・型枠（かたわく）：コンクリートを成形する枠\n・養生（ようじょう）：コンクリートの品質保護\n\n【工事関連】\n・墨出し（すみだし）：基準線を引く作業\n・配筋（はいきん）：鉄筋を配置する作業\n・打設（だせつ）：コンクリートを流し込む作業\n・左官（さかん）：壁や床を塗る作業",
    ],
    totalPages: 4,
  };
};

/**
 * All recommendation scenarios for local testing.
 * Change MOCK_RECOMMENDATION_INDEX to test different cases:
 *   0 = ASSESSMENT (no level)
 *   1 = VOCAB_PRACTICE (no level)
 *   2 = VOCAB_QUIZ (no level)
 *   3 = LEVEL_PRACTICE 1
 *   4 = LEVEL_QUIZ 3
 *   5 = null (all completed, no recommendation)
 */
const MOCK_RECOMMENDATION_INDEX = 5;

const MOCK_RECOMMENDATIONS: (TRecommendation | null)[] = [
  { publishedSubjectId: 1, subjectName: SUBJECT_NAMES[3], type: RECOMMENDATION_TYPE.ASSESSMENT, level: null, displayText: "次：レベル診断" },
  { publishedSubjectId: 1, subjectName: SUBJECT_NAMES[0], type: RECOMMENDATION_TYPE.VOCAB_PRACTICE, level: null, displayText: "次：単語練習" },
  { publishedSubjectId: 1, subjectName: SUBJECT_NAMES[0], type: RECOMMENDATION_TYPE.VOCAB_QUIZ, level: null, displayText: "次：単語テスト" },
  {
    publishedSubjectId: 1,
    subjectName: SUBJECT_NAMES[2],
    type: RECOMMENDATION_TYPE.LEVEL_PRACTICE,
    level: "lv3",
    displayText: `次：Lv1 ${t(LEVEL_NAMES[0])}の練習`,
  },
  {
    publishedSubjectId: 1,
    subjectName: SUBJECT_NAMES[1],
    type: RECOMMENDATION_TYPE.LEVEL_QUIZ,
    level: "lv5",
    displayText: `次：Lv3 ${t(LEVEL_NAMES[2])}のテスト`,
  },
  null,
];

/** Mock recommendation data from GET /learning/recommendations */
export const getRecommendationMock = async (): Promise<TRecommendation | null> => {
  await delay(300);
  return MOCK_RECOMMENDATIONS[MOCK_RECOMMENDATION_INDEX] ?? null;
};

/** Exported constants for test reuse */
export const MOCK_SUBJECTS = getSubjects();
